/**
 * Earnings Page (Leader Dashboard)
 *
 * 수익 공유 수입 페이지 (리더가 받은 돈)
 * - 수익 공유 수입 내역 테이블
 * - 기간별 필터 (week, month, all)
 * - 총 수입 요약 카드
 * - 팔로워별 수익 공유 내역
 * - 상태별 필터 (pending, completed, failed)
 *
 * @see docs/COPY_TRADING_FRONTEND_PLAN.md
 */

import React, { useState, useEffect, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { Table, Tag, Select, Statistic, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { ThemeContext } from '@/theme/ThemeProvider';
import { formatDecimal } from '@utils/formatDecimal';
// import { getMyEarnings, getEarningsSummary } from '../../api/copyTrading.real'; // TODO: Implement these functions
const getMyEarnings = async (_params?: any) => ({ success: true, data: { data: [], pagination: null } } as any);
const getEarningsSummary = async () => ({
  success: true,
  data: {
    totalEarned: 0,
    thisWeekEarned: 0,
    thisMonthEarned: 0,
    pendingAmount: 0,
    completedCount: 0,
    pendingCount: 0
  }
} as any);

import type { CopySettlement } from '../../../types/copyTrading';

const { Option } = Select;

export function EarningsPage() {
  const { t } = useTranslation();
  const { isDarkMode } = useContext(ThemeContext);

  const [earnings, setEarnings] = useState<CopySettlement[]>([]);
  const [summary, setSummary] = useState({
    totalEarned: 0,
    thisWeekEarned: 0,
    thisMonthEarned: 0,
    pendingAmount: 0,
    completedCount: 0,
    pendingCount: 0,
  });
  const [isLoading, setIsLoading] = useState(false);

  // 필터 상태
  const [periodFilter, setPeriodFilter] = useState<'week' | 'month' | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'completed' | 'failed'>('all');

  // ==========================================
  // Effects
  // ==========================================

  useEffect(() => {
    fetchEarningsData();
    fetchSummaryData();
  }, []);

  useEffect(() => {
    fetchEarningsData();
  }, [periodFilter, statusFilter]);

  // ==========================================
  // Handlers
  // ==========================================

  const fetchEarningsData = async () => {
    setIsLoading(true);
    try {
      const response = await getMyEarnings({
        period: periodFilter,
        status: statusFilter === 'all' ? undefined : statusFilter,
      });

      if (response.success) {
        setEarnings(response.data.data);
      } else {
        message.error(response.error?.message || '수익 조회 실패');
      }
    } catch (error) {
      console.error('Failed to fetch earnings:', error);
      message.error('수익 조회 실패');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSummaryData = async () => {
    try {
      const response = await getEarningsSummary();
      if (response.success) {
        setSummary(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  // ==========================================
  // Table Columns
  // ==========================================

  const columns: ColumnsType<CopySettlement> = useMemo(() => [
    {
      title: '정산 기간',
      key: 'period',
      render: (_, record) => (
        <div>
          <div>{new Date(record.settlementPeriodStart).toLocaleDateString()}</div>
          <div style={{ fontSize: 12, opacity: 0.7 }}>
            ~ {new Date(record.settlementPeriodEnd).toLocaleDateString()}
          </div>
        </div>
      ),
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusMap = {
          pending: { text: 'Pending', color: 'orange' },
          completed: { text: 'Completed', color: 'green' },
          failed: { text: 'Failed', color: 'red' },
        };
        const statusInfo = statusMap[status as keyof typeof statusMap];
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: '팔로워 수익',
      dataIndex: 'totalProfit',
      key: 'totalProfit',
      render: (profit: number) => (
        <span style={{ color: profit >= 0 ? '#22c55e' : '#ef4444' }}>
          {profit >= 0 ? '+' : ''}
          {formatDecimal(profit, 2)} USDT
        </span>
      ),
    },
    {
      title: '공유율',
      dataIndex: 'profitShareRate',
      key: 'profitShareRate',
      render: (rate: number) => `${formatDecimal(rate, 1)}%`,
    },
    {
      title: '내 수익',
      dataIndex: 'profitShareAmount',
      key: 'profitShareAmount',
      render: (amount: number) => (
        <span style={{ color: '#22c55e', fontWeight: 600, fontSize: 16 }}>
          +{formatDecimal(amount, 2)} USDT
        </span>
      ),
    },
    {
      title: '정산일',
      dataIndex: 'settledAt',
      key: 'settledAt',
      render: (date: string | null) => date ? new Date(date).toLocaleDateString() : '-',
    },
  ], []);

  // ==========================================
  // Render
  // ==========================================

  return (
    <>
      <PageWrapper>
      {/* Page Header */}
      <CompactHeader isDark={isDarkMode}>
        <HeaderLeft>
          <HeaderTitle>{t('earningsTitle')}</HeaderTitle>
          <HeaderDescription>{t('earningsDesc')}</HeaderDescription>
        </HeaderLeft>

        <StatsRow>
          <StatItem>
            <StatLabel>Total</StatLabel>
            <StatValue style={{ color: '#22c55e' }}>
              {formatDecimal(summary.totalEarned, 2)} USDT
            </StatValue>
          </StatItem>
          <StatDivider />
          <StatItem>
            <StatLabel>This Month</StatLabel>
            <StatValue style={{ color: '#22c55e' }}>
              {formatDecimal(summary.thisMonthEarned, 2)} USDT
            </StatValue>
          </StatItem>
        </StatsRow>
      </CompactHeader>

      {/* Summary Cards */}
      <SummaryGrid>
        <SummaryCard isDark={isDarkMode}>
          <Statistic
            title="총 수익"
            value={formatDecimal(summary.totalEarned, 2)}
            suffix="USDT"
            valueStyle={{ color: '#22c55e', fontWeight: 700 }}
          />
        </SummaryCard>

        <SummaryCard isDark={isDarkMode}>
          <Statistic
            title="이번 주 수익"
            value={formatDecimal(summary.thisWeekEarned, 2)}
            suffix="USDT"
            valueStyle={{ color: '#22c55e' }}
          />
        </SummaryCard>

        <SummaryCard isDark={isDarkMode}>
          <Statistic
            title="이번 달 수익"
            value={formatDecimal(summary.thisMonthEarned, 2)}
            suffix="USDT"
            valueStyle={{ color: '#22c55e' }}
          />
        </SummaryCard>

        <SummaryCard isDark={isDarkMode}>
          <Statistic
            title="대기 중"
            value={formatDecimal(summary.pendingAmount, 2)}
            suffix="USDT"
            valueStyle={{ color: '#ff9131' }}
          />
          <PendingCount>({summary.pendingCount}건)</PendingCount>
        </SummaryCard>
      </SummaryGrid>

      {/* Filters */}
      <FilterSection isDark={isDarkMode}>
        <FilterGroup>
          <label>기간:</label>
          <Select
            value={periodFilter}
            onChange={(value) => setPeriodFilter(value)}
            style={{ width: 120 }}
          >
            <Option value="week">최근 7일</Option>
            <Option value="month">최근 30일</Option>
            <Option value="all">전체</Option>
          </Select>
        </FilterGroup>

        <FilterGroup>
          <label>상태:</label>
          <Select
            value={statusFilter}
            onChange={(value) => setStatusFilter(value)}
            style={{ width: 120 }}
          >
            <Option value="all">전체</Option>
            <Option value="completed">Completed</Option>
            <Option value="pending">Pending</Option>
            <Option value="failed">Failed</Option>
          </Select>
        </FilterGroup>

        <FilterInfo>
          총 {earnings.length}건 | 완료: {summary.completedCount}건 | 대기: {summary.pendingCount}건
        </FilterInfo>
      </FilterSection>

      {/* Earnings Table */}
      <Section isDark={isDarkMode}>
        <SectionTitle>수익 공유 내역</SectionTitle>
        <Table
          columns={columns}
          dataSource={earnings}
          rowKey={(record) => record.id}
          loading={isLoading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `총 ${total}건`,
          }}
        />
      </Section>
      </PageWrapper>
    </>
  );
}

// ==========================================
// Styled Components
// ==========================================

const PageWrapper = styled.div`
  display: flex;
  width: 1200px;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
`;

const CompactHeader = styled.div<{ isDark: boolean }>`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(200px, 1fr) auto;
  align-items: center;
  gap: 24px;
  margin-bottom: 20px;
  padding: 20px 24px;
  box-sizing: border-box;
  background: ${({ isDark }) =>
    isDark
      ? 'linear-gradient(135deg, rgba(255, 145, 49, 0.05) 0%, rgba(20, 17, 34, 0.05) 100%), #1a1a1a'
      : 'linear-gradient(135deg, rgba(255, 145, 49, 0.03) 0%, rgba(255, 255, 255, 1) 100%)'
  };
  border-radius: 12px;
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const HeaderTitle = styled.h1`
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: ${({ theme }) => theme.text};
`;

const HeaderDescription = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: auto auto auto;
  align-items: center;
  gap: 20px;
  justify-self: end;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
    width: 100%;
    justify-self: stretch;
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  min-width: 110px;

  @media (max-width: 768px) {
    align-items: center;
    min-width: unset;
  }
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
  white-space: nowrap;
`;

const StatDivider = styled.div`
  width: 1px;
  height: 32px;
  background-color: ${({ theme }) => theme.text};
  opacity: 0.1;

  @media (max-width: 768px) {
    display: none;
  }
`;

const SummaryGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const SummaryCard = styled.div<{ isDark: boolean }>`
  width: 100%;
  padding: 24px;
  box-sizing: border-box;
  border-radius: 12px;
  background: ${({ isDark }) =>
    isDark
      ? 'linear-gradient(135deg, rgba(255, 145, 49, 0.03) 0%, rgba(20, 17, 34, 0.03) 100%), #1a1a1a'
      : 'linear-gradient(135deg, rgba(255, 145, 49, 0.02) 0%, rgba(255, 255, 255, 1) 100%)'
  };
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #f0f0f0')};
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ isDark }) =>
      isDark ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.08)'};
  }

  .ant-statistic-title {
    color: ${({ theme }) => theme.text};
    opacity: 0.7;
  }

  .ant-statistic-content {
    color: ${({ theme }) => theme.text};
  }
`;

const PendingCount = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
  margin-top: 4px;
`;

const FilterSection = styled.div<{ isDark: boolean }>`
  width: 100%;
  display: flex;
  gap: 16px;
  align-items: center;
  padding: 16px 20px;
  box-sizing: border-box;
  background: ${({ isDark }) => (isDark ? '#1a1a1a' : '#fff')};
  border-radius: 12px;
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};
  margin-bottom: 16px;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  label {
    font-size: 14px;
    color: ${({ theme }) => theme.text};
    opacity: 0.7;
  }
`;

const FilterInfo = styled.div`
  margin-left: auto;
  font-size: 13px;
  color: ${({ theme }) => theme.text};
  opacity: 0.7;
`;

const Section = styled.div<{ isDark: boolean }>`
  width: 100%;
  margin-bottom: 32px;
  padding: 24px;
  box-sizing: border-box;
  border-radius: 12px;
  background-color: ${({ isDark }) => (isDark ? '#1a1a1a' : '#fff')};
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  color: ${({ theme }) => theme.text};
`;
